import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
}                           from 'react-query';
import { AxiosError }       from 'axios';
import {
  callApi,
  requestAttachments,
}                           from '@utils/apiCaller';
import { showErrorMessage } from '@utils/commonFunctions';
import { Notification }     from '@components/Notification/Notification';
import {
  TVinHistory,
  TMonthlyDashboardStats,
  ITotalInnerStats,
  IDownloadStatsPayload,
}                           from '@typings/dashboard';
import { ISearchParams }    from '@typings/search';
import { ICuidPayload }     from '@typings/accountManagement';
import { EStatsType }       from '@enums/dashboard';

const getStatisticsPayload = (cuid: string | null): ICuidPayload | null => {
  if (!cuid) {
    return null;
  }

  return { cuid };
};

const DOWNLOAD_ROUTE: Record<EStatsType, string> = {
  [EStatsType.InnerTotal]   : '/inner/total',
  [EStatsType.InnerMonthly] : '/inner/month',
  [EStatsType.Services]     : '/services',
};

// #region FETCH VIN HISTORY
const fetchVinHistory = async (params: ISearchParams): Promise<TVinHistory> => {
  try {
    const { data } = await callApi<TVinHistory>(
      'intraDashboard/vin-history',
      'post',
      params,
    );

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchVinHistory = (
  params  : ISearchParams,
  config? : UseQueryOptions<TVinHistory, AxiosError>,
): UseQueryResult<TVinHistory, AxiosError> => useQuery(
  ['vin-history', params],
  () => fetchVinHistory(params),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region FETCH SERVICES STATISTICS
const fetchServicesStatistics = async (cuid: string | null): Promise<TMonthlyDashboardStats> => {
  try {
    const { data } = await callApi<TMonthlyDashboardStats>(
      'IntraDashboard/statistic/services',
      'get',
      getStatisticsPayload(cuid),
    );

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchServicesStatistics = (
  cuid    : string | null,
  config? : UseQueryOptions<TMonthlyDashboardStats, AxiosError>,
): UseQueryResult<TMonthlyDashboardStats, AxiosError> => useQuery(
  ['services-stats', cuid],
  () => fetchServicesStatistics(cuid),
  {
    refetchOnWindowFocus : false,
    retry                : false,
    ...config,
  },
);
// #endregion

// #region FETCH INNER MONTH STATISTICS
const fetchInnerMonthStatistics = async (cuid: string | null): Promise<TMonthlyDashboardStats> => {
  try {
    const { data } = await callApi<TMonthlyDashboardStats>(
      'IntraDashboard/statistic/inner/month',
      'get',
      getStatisticsPayload(cuid),
    );

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchInnerMonthStatistics = (
  cuid    : string | null,
  config? : UseQueryOptions<TMonthlyDashboardStats, AxiosError>,
): UseQueryResult<TMonthlyDashboardStats, AxiosError> => useQuery(
  ['inner-month-stats', cuid],
  () => fetchInnerMonthStatistics(cuid),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region FETCH INNER TOTAL STATISTICS
const fetchInnerTotalStatistics = async (cuid: string | null): Promise<ITotalInnerStats> => {
  try {
    const { data } = await callApi<ITotalInnerStats>(
      'IntraDashboard/statistic/inner/total',
      'get',
      getStatisticsPayload(cuid),
    );

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchInnerTotalStatistics = (
  cuid    : string | null,
  config? : UseQueryOptions<ITotalInnerStats, AxiosError>,
): UseQueryResult<ITotalInnerStats, AxiosError> => useQuery(
  ['inner-total-stats', cuid],
  () => fetchInnerTotalStatistics(cuid),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region DOWNLOAD STATISTICS EXCEL
const downloadStatisticsExcel = async ({
  cuid,
  statsType,
}: IDownloadStatsPayload): Promise<void> => {
  try {
    await requestAttachments(
      `IntraDashboard/statistic${DOWNLOAD_ROUTE[statsType]}/excel`,
      'attachment',
      getStatisticsPayload(cuid),
    );
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useDownloadStatisticsExcel = (
  config: UseMutationOptions<void, AxiosError, IDownloadStatsPayload> = {},
): UseMutationResult<void, AxiosError, IDownloadStatsPayload> => useMutation(
  downloadStatisticsExcel,
  config,
);
// #endregion
